/**
 * Standard list of Portuguese particles that should remain lowercase in proper
 * names (when not at the start of the string).
 */
export const PT_BR_LOWERCASE_PARTICLES = [
  "de",
  "da",
  "das",
  "do",
  "dos",
  "no",
  "nos",
  "na",
  "nas",
  "e",
  "com",
  "via",
];

// Unidades que ficam como a loja digitou depois de um número (Pedro, 15/10/2026: «açaí 500 ml» saía «Açaí 500 Ml»).
// Lista das que aparecem nos nomes de produto do banco; palavra inteira ("litros", "gramas") segue a regra comum, e o
// "x" de "2 X Egg" também (é o lanche).
const UNIDADES_DE_MEDIDA = ["ml", "l", "lt", "lts", "kg", "g", "gr", "grs", "mg", "cm", "mm", "un", "und", "unds", "unid", "pct", "pç", "pçs"];
const NUMERO = /^(?:\d+(?:[.,/]\d+)?|\d*[½¼¾])$/;
const NUMERO_E_LETRAS = /^(?:\d+(?:[.,]\d+)?|\d*[½¼¾])(\p{L}+)$/u;

/**
 * A palavra `i` é uma unidade de medida depois de um número ("500 ml", "1 kg", "2 l") ou colada nele ("500ml",
 * "2L"): quem formata o nome a deixa como veio. Com `prefixo` (digitação ao vivo) vale também o começo de uma unidade
 * — senão o "m" de "ml" virava "M" antes do "l" chegar e o "Ml" ficava.
 */
export function unidadeDeMedida(palavras: string[], i: number, prefixo = false): boolean {
  const palavra = (palavras[i] ?? "").replace(/[.,;:!?)]+$/, "");
  if (!palavra) return false;
  const ehUnidade = (s: string) => {
    const u = s.toLocaleLowerCase("pt-BR");
    return prefixo ? UNIDADES_DE_MEDIDA.some((x) => x.startsWith(u)) : UNIDADES_DE_MEDIDA.includes(u);
  };
  const colada = NUMERO_E_LETRAS.exec(palavra);
  if (colada) return ehUnidade(colada[1]!);
  let j = i - 1;
  while (j >= 0 && !palavras[j]) j--;
  return j >= 0 && NUMERO.test(palavras[j]!) && ehUnidade(palavra);
}

/**
 * Capitalizes every word (Title Case). Does not lowercase any particles.
 * Use this for live-typing input controls where the user expects each word
 * to capitalize as they type. Units after a number stay as typed.
 */
export function capitalizeWords(text: string): string {
  return text
    .split(" ")
    .map((word, i, palavras) => {
      if (!word) return word;
      if (unidadeDeMedida(palavras, i, true)) return word;
      return word.charAt(0).toLocaleUpperCase("pt-BR") + word.slice(1).toLocaleLowerCase("pt-BR");
    })
    .join(" ");
}

/**
 * Canonicalizes a proper name for storage: trims, collapses whitespace,
 * Title-Cases each word, and lowercases known Portuguese particles when
 * they don't start the string. Uses pt-BR locale for accent-aware casing.
 *
 * Equivalent to the legacy `capitalizeName` from the API server.
 */
export function formatNameForSave(text: string): string {
  const trimmed = (text || "").trim().replace(/\s+/g, " ");
  if (!trimmed) return "";
  return trimmed
    .split(" ")
    .map((word, index, palavras) => {
      if (!word) return word;
      if (unidadeDeMedida(palavras, index)) return word;
      const lower = word.toLocaleLowerCase("pt-BR");
      if (index > 0 && PT_BR_LOWERCASE_PARTICLES.includes(lower)) return lower;
      return lower.charAt(0).toLocaleUpperCase("pt-BR") + lower.slice(1);
    })
    .join(" ");
}

/** Backend-friendly alias for `formatNameForSave`. */
export const capitalizeName = formatNameForSave;
